import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AuthTokens,
  LoginPayload,
  UserMembership,
  UserPrincipal,
} from '../types/data-models';

export interface LoginRequest {
  email: string;
  password: string;
  /** Optional tenant id when the user has multiple memberships. */
  tenantId?: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
  tenantName: string;
}

/** How an "ask for access" request says they found Vibelin. Mirrors the backend `UserSource` enum. */
export type UserSource =
  | 'GOOGLE_SEARCH'
  | 'TIKTOK'
  | 'LINKEDIN'
  | 'TWITTER_X'
  | 'FRIEND_REFERRAL'
  | 'BLOG_ARTICLE'
  | 'CONFERENCE_EVENT'
  | 'OTHER';

export interface RequestAccessRequest {
  email: string;
  sources: UserSource[];
}

export interface LoginResult {
  user: UserPrincipal;
  tokens: AuthTokens;
  memberships: UserMembership[];
}

/**
 * A Google identity with no account yet: the backend hands back a
 * short-lived token that the "name your workspace" step exchanges for a
 * real account via `completeGoogleSignup`.
 */
export interface GoogleSignupRequired {
  status: 'signup_required';
  signupToken: string;
  email: string;
  name: string | null;
}

export type GoogleAuthResult = ({ status: 'authenticated' } & LoginResult) | GoogleSignupRequired;

export function toPrincipal(p: LoginPayload['user']): UserPrincipal {
  return {
    id: p.userId,
    email: p.email,
    name: p.name ?? null,
    tenantId: p.tenantId,
    role: p.roles?.[0] ?? '',
    roles: p.roles ?? [],
  };
}

/**
 * Memberships now come back in the body of every auth response. They used to be
 * hard-coded to `[]` here, which left the workspace switcher permanently empty.
 */
function readMemberships(body: { memberships?: UserMembership[] } | undefined): UserMembership[] {
  return body?.memberships ?? [];
}

export const authService = {
  async login(payload: LoginRequest): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload>>('/auth/login', payload);
    const body = res.data?.data ?? (res.data as unknown as LoginPayload | undefined);
    if (!body || !body.accessToken) throw new Error('Invalid login response: missing accessToken');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: readMemberships(body),
    };
  },

  async register(payload: RegisterRequest): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<{ user: LoginPayload['user']; tokens: LoginPayload }>>(
      '/auth/register',
      payload,
    );
    const body = res.data?.data;
    if (!body || !body.tokens?.accessToken) {
      throw new Error('Invalid register response: missing accessToken');
    }
    return {
      user: toPrincipal(body.user),
      tokens: {
        accessToken: body.tokens.accessToken,
        refreshToken: body.tokens.refreshToken,
      },
      memberships: readMemberships(body as { memberships?: UserMembership[] }),
    };
  },

  /** Exchange a Google ID token for our own tokens, or for a signup token. */
  async loginWithGoogle(idToken: string): Promise<GoogleAuthResult> {
    const res = await api.post<OneEnvelope<LoginPayload | GoogleSignupRequired>>('/auth/google', {
      idToken,
    });
    const body = res.data?.data;
    if (!body) throw new Error('Invalid Google sign-in response');

    if ((body as GoogleSignupRequired).status === 'signup_required') {
      return body as GoogleSignupRequired;
    }
    const payload = body as LoginPayload;
    if (!payload.accessToken) throw new Error('Invalid Google sign-in response: missing accessToken');
    return {
      status: 'authenticated',
      user: toPrincipal(payload.user),
      tokens: { accessToken: payload.accessToken, refreshToken: payload.refreshToken },
      memberships: readMemberships(payload),
    };
  },

  /** Finish Google signup by naming the new workspace. */
  async completeGoogleSignup(signupToken: string, tenantName: string): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload>>('/auth/google/complete', {
      signupToken,
      tenantName,
    });
    const body = res.data?.data;
    if (!body?.accessToken) throw new Error('Invalid signup response: missing accessToken');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: readMemberships(body),
    };
  },

  async switchTenant(tenantId: number): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload>>('/auth/switch-tenant', { tenantId });
    const body = res.data?.data ?? (res.data as unknown as LoginPayload | undefined);
    if (!body || !body.accessToken) throw new Error('Invalid switch-tenant response');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: readMemberships(body),
    };
  },

  /** Submit a self-serve "ask for access" request. Resolves with no session — the account stays PENDING until reviewed. */
  async requestAccess(payload: RequestAccessRequest): Promise<void> {
    await api.post('/auth/request-access', payload);
  },

  /** Fallback for callers that need memberships without re-authenticating. */
  async memberships(): Promise<UserMembership[]> {
    const res = await api.get<OneEnvelope<UserMembership[]>>('/auth/memberships');
    return res.data?.data ?? [];
  },

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const res = await api.post<OneEnvelope<AuthTokens> | AuthTokens>('/auth/refresh', { refreshToken });
    const wrapped = res.data as OneEnvelope<AuthTokens>;
    return wrapped?.data ?? (res.data as AuthTokens);
  },
};
